"use client";

import { useMediaQuery } from "@/hooks/use-media-query";

/**
 * Classe de largeur de l'écran.
 *
 * Trois paliers seulement : au-delà, chaque surface finit par inventer son
 * propre seuil et le produit se met à respirer différemment d'un écran à
 * l'autre pour la même largeur.
 */
export type Breakpoint = "compact" | "medium" | "expanded";

/**
 * Bornes basses (min-width, **en rem**) de chaque palier au-dessus de
 * `compact`. Soit 768 px et 1024 px à la taille de police racine par défaut.
 *
 * En rem et non en px, parce que c'est l'unité de `md:` et `lg:` chez Tailwind
 * v4 (`--breakpoint-md: 48rem`). Les exprimer en px paraissait équivalent et ne
 * l'était pas : sur un navigateur réglé à 20 px de police de base, `md:`
 * bascule à 960 px pendant que le hook annonçait encore `medium` dès 800 — le
 * CSS et le JS décrivaient alors deux frontières différentes. C'est ce qui
 * faisait régresser silencieusement l'Échap en deux temps de la bibliothèque :
 * les volets passaient en une colonne sans que `isCompact` le sache, donc
 * Échap fermait la modale au lieu de revenir à la liste.
 */
export const BREAKPOINTS = {
  medium: 48,
  expanded: 64,
} as const;

const MEDIUM_QUERY = `(min-width: ${BREAKPOINTS.medium}rem)`;
const EXPANDED_QUERY = `(min-width: ${BREAKPOINTS.expanded}rem)`;

/**
 * Palier de largeur courant, réévalué à chaque redimensionnement.
 *
 * - `compact` : < 48 rem (768 px par défaut)
 * - `medium` : 48 rem → 64 rem (768 px → 1023 px par défaut)
 * - `expanded` : ≥ 64 rem (1024 px par défaut)
 *
 * Repose sur {@link useMediaQuery}, qui renvoie `false` au premier rendu (SSR
 * et première frame client) : le hook annonce donc `compact` avant de se
 * corriger sur l'effet de montage. Un composant qui branche sa mise en page
 * dessus doit neutraliser ce battement d'une frame — retarder les transitions
 * jusqu'après le premier commit, ou ne rien peindre tant que le palier n'est
 * pas résolu — sinon un poste desktop voit brièvement la version mobile.
 */
export function useBreakpoint(): Breakpoint {
  const isMedium = useMediaQuery(MEDIUM_QUERY);
  const isExpanded = useMediaQuery(EXPANDED_QUERY);

  if (isExpanded) {
    return "expanded";
  }

  if (isMedium) {
    return "medium";
  }

  return "compact";
}
